import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReportType, AssessmentDetail, AssessmentStatus, LifestyleRating, LifestyleStatus, ResultView, ScreeningRow, SectionDef } from '../../shared/types'
import { LIFESTYLE_AREAS } from '../../shared/types'
import { api, ApiRequestError } from '../api'
import { Card, ErrorNote, Pill, Spinner } from '../components'
import { bytes, dateLabel, dateTimeLabel } from '../format'
import { useSession } from '../session-context'
import { formatValue } from '../../report/format'

const STATUS_LABEL: Record<AssessmentStatus, string> = { draft: 'Draft', in_review: 'In review', signed: 'Signed', delivered: 'In Semble' }

type Tab = 'results' | 'narrative' | 'report' | 'sources'

export function AssessmentScreen({ id, onBack, onOpenPatient }: { id: number; onBack: () => void; onOpenPatient: (id: number) => void }) {
  const { can } = useSession()
  const [d, setD] = useState<AssessmentDetail | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('results')
  const [flash, setFlash] = useState<string | null>(null)

  const load = useCallback(async () => setD(await api.assessment(id)), [id])
  useEffect(() => { load().catch(setError) }, [load])

  const run = useCallback(async (key: string, fn: () => Promise<AssessmentDetail | void>, done?: string) => {
    setBusy(key); setError(null)
    try { const r = await fn(); if (r) setD(r); else await load(); if (done) setFlash(done) } catch (err) { setError(err) } finally { setBusy(null) }
  }, [load])

  if (d === null) return error === null ? <Spinner /> : <ErrorNote error={error} />
  const locked = d.assessment.status === 'signed' || d.assessment.status === 'delivered'
  const p = d.patient

  return (
    <>
      <div className="crumbs"><button type="button" className="linkish" onClick={onBack}>← Patients</button> · <button type="button" className="linkish" onClick={() => onOpenPatient(p.id)}>{p.firstName} {p.lastName}: all screens and trends</button></div>
      <Card
        title={`${p.lastName}, ${p.firstName}`}
        subtitle={`${d.reportOptions.type === 'summary' ? 'Short results report' : 'Prevent51 assessment'} dated ${dateLabel(d.assessment.assessmentDate)} · DOB ${dateLabel(p.dob)}${p.sembleId ? ` · Semble ${p.sembleId}` : ''}`}
        actions={<>
          <Pill tone={locked ? 'ok' : d.assessment.status === 'in_review' ? 'warn' : 'neutral'}>{STATUS_LABEL[d.assessment.status]}</Pill>
          {locked && can('clinician') && <button type="button" onClick={() => void run('reopen', () => api.setStatus(id, 'in_review'), 'Reopened. The signed version is kept.')}>Reopen for changes</button>}
          {!locked && d.versions.length === 0 && can('clinician') && <button type="button" className="quiet" onClick={() => { if (window.confirm('Delete this assessment and everything entered on it? It has never been signed.')) void run('delete', async () => { await api.deleteAssessment(id); onBack() }) }}>Delete draft</button>}
        </>}
      >
        {p.sex === 'unknown' && can('nurse') && (
          <p className="warn-note">Sex at birth is not set, so the female-specific ranges (Hb, creatinine, body fat) cannot be applied.
            {' '}<button type="button" onClick={() => void run('sex', async () => { await api.updatePatient(p.id, { sex: 'female' }) })}>Set female</button></p>
        )}
        {flash !== null && <p className="flash">{flash} <button type="button" className="linkish" onClick={() => setFlash(null)}>dismiss</button></p>}
        <ErrorNote error={error} />
        <nav className="subtabs">
          {(['results', 'narrative', 'report', 'sources'] as Tab[]).map((t) => (
            <button key={t} type="button" className={t === tab ? 'active' : undefined} onClick={() => setTab(t)}>
              {t === 'results' ? `Results (${d.results.filter((r) => r.value !== null || r.valueText !== null).length})` : t === 'narrative' ? 'Findings, lifestyle & comments' : t === 'report' ? `Report${d.versions.length > 0 ? ` (v${d.versions[0]?.version})` : ''}` : `Sources & exceptions (${d.exceptions.filter((e) => e.resolvedAt === null).length})`}
            </button>
          ))}
        </nav>
      </Card>

      {tab === 'results' && <ResultsTab d={d} locked={locked} busy={busy} run={run} canEdit={can('nurse')} canBand={can('clinician')} />}
      {tab === 'narrative' && <NarrativeTab d={d} locked={locked} busy={busy} run={run} canEdit={can('nurse')} canComment={can('clinician')} />}
      {tab === 'report' && <ReportTab d={d} busy={busy} run={run} canSign={can('clinician')} canEdit={can('nurse') && !locked} />}
      {tab === 'sources' && <SourcesTab d={d} locked={locked} busy={busy} run={run} canEdit={can('nurse')} />}
    </>
  )
}

type Run = (key: string, fn: () => Promise<AssessmentDetail | void>, done?: string) => Promise<void>

// --- Results ------------------------------------------------------------------------------

function ResultsTab({ d, locked, busy, run, canEdit, canBand }: { d: AssessmentDetail; locked: boolean; busy: string | null; run: Run; canEdit: boolean; canBand: boolean }) {
  const parents = useMemo(() => { const out: (string | null)[] = []; for (const s of d.sections) if (!out.includes(s.parentTitle)) out.push(s.parentTitle); return out }, [d.sections])
  return (
    <>
      {canEdit && !locked && <ObservationsForm d={d} busy={busy} run={run} />}
      {parents.map((parent) => (
        <Card key={parent ?? 'none'} title={parent ?? 'Other'}>
          {d.sections.filter((s) => s.parentTitle === parent).map((s) => (
            <SectionResults key={s.code} d={d} s={s} locked={locked} busy={busy} run={run} canEdit={canEdit} canBand={canBand} />
          ))}
        </Card>
      ))}
    </>
  )
}

function SectionResults({ d, s, locked, busy, run, canEdit, canBand }: { d: AssessmentDetail; s: SectionDef; locked: boolean; busy: string | null; run: Run; canEdit: boolean; canBand: boolean }) {
  const rows = d.results.filter((r) => r.sectionCode === s.code)
  return (
    <div className="section-block">
      <h3>{s.title}</h3>
      <table className="results">
        <thead><tr><th>Marker</th><th>Value</th><th>Clinic51 band</th><th className="muted">Lab range / flag</th><th>Source</th></tr></thead>
        <tbody>{rows.map((r) => <ResultRow key={r.markerCode} d={d} r={r} locked={locked} busy={busy} run={run} canEdit={canEdit} canBand={canBand} />)}</tbody>
      </table>
    </div>
  )
}

function ResultRow({ d, r, locked, busy, run, canEdit, canBand }: { d: AssessmentDetail; r: ResultView; locked: boolean; busy: string | null; run: Run; canEdit: boolean; canBand: boolean }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const present = r.value !== null || r.valueText !== null
  const save = (): void => {
    const text = draft.trim()
    if (text === '') return
    void run(`set-${r.markerCode}`, () => api.setResults(d.assessment.id, [r.valueKind === 'text'
      ? { markerCode: r.markerCode, valueText: text, measuredOn: d.assessment.assessmentDate }
      : { markerCode: r.markerCode, value: Number(text), measuredOn: d.assessment.assessmentDate }])).then(() => setEditing(false))
  }
  return (
    <tr className={present ? undefined : 'missing'}>
      <td>{r.name}{r.isDerived && <span className="muted small"> · calculated</span>}{r.note !== null && r.note !== '' && <div className="muted small">{r.note}</div>}</td>
      <td className="value">
        {editing ? (
          <span className="row">
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }} placeholder={r.unit} style={{ width: '7em' }} />
            <button type="button" onClick={save} disabled={busy !== null}>Save</button>
            <button type="button" className="quiet" onClick={() => setEditing(false)}>Cancel</button>
          </span>
        ) : (
          <>
            <b>{present ? formatValue(r) : '—'}</b> <span className="muted">{present ? r.unit : ''}</span>
            {canEdit && !locked && !r.isDerived && <button type="button" className="quiet" onClick={() => { setDraft(r.valueKind === 'text' ? (r.valueText ?? '') : r.value === null ? '' : String(r.value)); setEditing(true) }}>{present ? 'edit' : 'enter'}</button>}
            {canEdit && !locked && !r.isDerived && present && <button type="button" className="quiet" onClick={() => void run(`del-${r.markerCode}`, () => api.deleteResult(d.assessment.id, r.markerCode))}>clear</button>}
          </>
        )}
      </td>
      <td>
        {r.band !== null ? <span className={`chip ${r.band.colour}`}>{r.band.label}{r.band.overridden && ' (set by clinician)'}</span>
          : present && r.bands.length > 0 && r.valueKind === 'numeric' ? <span className="chip grey">outside every band</span>
          : present && r.bands.length === 0 ? <span className="muted small">no bands</span> : null}
        {canBand && !locked && present && (r.bands.length === 0 || r.band?.overridden === true) && (
          <select className="quiet" value={r.band?.overridden ? r.band.label : ''} onChange={(e) => void run(`band-${r.markerCode}`, () => api.setBand(d.assessment.id, r.markerCode, e.target.value === '' ? null : e.target.value))}>
            <option value="">band…</option>{['Weak', 'Normal', 'Strong', 'Optimal', 'Borderline', 'High', 'Low'].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
        {r.bands.length > 0 && <div className="bandstrip tiny">{r.bands.map((b) => <span key={b.label} className={`seg ${b.colour}${r.band?.label === b.label ? ' on' : ''}`} title={b.display} />)}</div>}
      </td>
      <td className="muted small">{r.labRangeText ?? ''}{r.labFlag !== null && r.labFlag !== 'N' && <b> {r.labFlag}</b>}</td>
      <td className="muted small">{r.source === null ? '' : <span title={r.source.locator ?? ''}>{r.source.label}{r.measuredOn !== null && ` · ${dateLabel(r.measuredOn)}`}</span>}</td>
    </tr>
  )
}

// --- Observations form -------------------------------------------------------------------------

/** The nurse's eight measurements in one go, the fallback for when the Semble consultation does not come through. */
const OBS_FIELDS = [
  { code: 'height', label: 'Height', min: 100, max: 230 },
  { code: 'weight', label: 'Weight', min: 25, max: 300 },
  { code: 'waist', label: 'Waist', min: 40, max: 200 },
  { code: 'bp_systolic', label: 'BP systolic', min: 60, max: 260 },
  { code: 'bp_diastolic', label: 'BP diastolic', min: 30, max: 160 },
  { code: 'pulse', label: 'Pulse', min: 25, max: 220 },
  { code: 'grip_strength', label: 'Grip strength', min: 1, max: 100 },
] as const
const RHYTHMS = ['Normal Sinus Rhythm', 'Irregular', 'Regularly irregular', 'Irregularly irregular']

function ObservationsForm({ d, busy, run }: { d: AssessmentDetail; busy: string | null; run: Run }) {
  const byCode = useMemo(() => new Map(d.results.map((r) => [r.markerCode, r])), [d.results])
  const initial = useCallback((): Record<string, string> => {
    const o: Record<string, string> = {}
    for (const f of OBS_FIELDS) { const r = byCode.get(f.code); o[f.code] = r?.value === null || r?.value === undefined ? '' : String(r.value) }
    o['pulse_rhythm'] = byCode.get('pulse_rhythm')?.valueText ?? ''
    const dated = OBS_FIELDS.map((f) => byCode.get(f.code)?.measuredOn ?? null).find((x) => x !== null)
    o['date'] = dated ?? d.assessment.assessmentDate
    return o
  }, [byCode, d.assessment.assessmentDate])
  const [open, setOpen] = useState(() => OBS_FIELDS.every((f) => (byCode.get(f.code)?.value ?? null) === null))
  const [v, setV] = useState<Record<string, string>>(initial)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => { setV(initial()) }, [initial])
  const set = (k: string, val: string): void => setV((o) => ({ ...o, [k]: val }))

  const save = (): void => {
    const values: Parameters<typeof api.setResults>[1] = []
    for (const f of OBS_FIELDS) {
      const t = v[f.code]?.trim() ?? ''
      if (t === '') continue
      const n = Number(t)
      if (!Number.isFinite(n)) { setProblem(`${f.label}: "${t}" is not a number`); return }
      if (n < f.min || n > f.max) { setProblem(`${f.label}: ${n} is outside ${f.min}–${f.max} ${byCode.get(f.code)?.unit ?? ''}. Check the reading.`); return }
      values.push({ markerCode: f.code, value: n, measuredOn: v['date'] || null, sourceLocator: 'Observations form' })
    }
    const rhythm = v['pulse_rhythm']?.trim() ?? ''
    if (rhythm !== '') values.push({ markerCode: 'pulse_rhythm', valueText: rhythm, measuredOn: v['date'] || null, sourceLocator: 'Observations form' })
    if (values.length === 0) { setProblem('Nothing to save: every box is empty.'); return }
    setProblem(null)
    void run('observations', () => api.setResults(d.assessment.id, values)).then(() => setOpen(false))
  }
  const entered = OBS_FIELDS.filter((f) => (byCode.get(f.code)?.value ?? null) !== null).length + (byCode.get('pulse_rhythm')?.valueText ? 1 : 0)

  return (
    <Card title="Nurse observations" subtitle={`${entered} of ${OBS_FIELDS.length + 1} recorded. BMI and waist-to-height ratio are worked out from these.`}
      actions={<button type="button" className="link-button" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : entered === 0 ? 'Enter observations' : 'Edit all'}</button>}>
      {open && (
        <div className="obs-form">
          <label className="field obs-date"><span>Date measured</span><input type="date" value={v['date'] ?? ''} onChange={(e) => set('date', e.target.value)} /></label>
          <div className="obs-grid">
            {OBS_FIELDS.map((f) => (
              <label key={f.code} className="field">
                <span>{f.label} <small className="muted">{byCode.get(f.code)?.unit}</small></span>
                <input inputMode="decimal" value={v[f.code] ?? ''} onChange={(e) => set(f.code, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save() }} placeholder="—" />
              </label>
            ))}
            <label className="field">
              <span>Pulse rhythm</span>
              <input list="rhythms" value={v['pulse_rhythm'] ?? ''} onChange={(e) => set('pulse_rhythm', e.target.value)} placeholder="—" />
              <datalist id="rhythms">{RHYTHMS.map((r) => <option key={r} value={r} />)}</datalist>
            </label>
          </div>
          {problem !== null && <p className="warn-note small">{problem}</p>}
          <div className="row">
            <button type="button" onClick={save} disabled={busy !== null}>{busy === 'observations' ? 'Saving…' : 'Save observations'}</button>
            <button type="button" className="quiet" onClick={() => { setV(initial()); setProblem(null) }}>Reset</button>
            <span className="muted small">Empty boxes are left as they are; they do not clear a value. Use <em>clear</em> on the row below for that.</span>
          </div>
        </div>
      )}
    </Card>
  )
}

// --- Narrative --------------------------------------------------------------------------------

function TextField({ label, value, onSave, disabled, rows = 4, hint }: { label: string; value: string; onSave: (v: string) => void; disabled: boolean; rows?: number; hint?: string }) {
  const [v, setV] = useState(value)
  useEffect(() => setV(value), [value])
  return (
    <label className="field">
      <span>{label}{hint !== undefined && <span className="muted small"> · {hint}</span>}</span>
      <textarea rows={rows} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== value) onSave(v) }} disabled={disabled} />
    </label>
  )
}

function NarrativeTab({ d, locked, busy, run, canEdit, canComment }: { d: AssessmentDetail; locked: boolean; busy: string | null; run: Run; canEdit: boolean; canComment: boolean }) {
  const id = d.assessment.id
  const n = d.narrative
  const saveN = (key: string) => (v: string) => void run(`n-${key}`, () => api.updateNarrative(id, { [key]: v }))
  const [ratings, setRatings] = useState<LifestyleRating[]>(d.lifestyle)
  useEffect(() => setRatings(d.lifestyle), [d.lifestyle])
  const [screening, setScreening] = useState<ScreeningRow[]>(d.screening)
  useEffect(() => setScreening(d.screening), [d.screening])
  const ro = locked || !canEdit

  return (
    <>
      <Card title="Cover letter">
        <TextField label="Key findings" hint="one per line; they become the bullet list on page 1" value={n.keyFindings} onSave={saveN('keyFindings')} disabled={ro} rows={7} />
        <TextField label="Introduction" hint="leave blank for the standard wording" value={n.intro} onSave={saveN('intro')} disabled={ro} rows={3} />
        <TextField label="Vision for future health" hint="the statement the patient chose on the questionnaire" value={n.visionStatement} onSave={saveN('visionStatement')} disabled={ro} rows={2} />
        <TextField label="Your genetics" hint="family history paragraph" value={n.geneticsText} onSave={saveN('geneticsText')} disabled={ro} rows={5} />
        <TextField label="Lifestyle introduction" hint="leave blank for the standard wording" value={n.lifestyleIntro} onSave={saveN('lifestyleIntro')} disabled={ro} rows={3} />
        <div className="grid3">
          <label className="field"><span>Signing clinician</span><input defaultValue={n.clinicianName} onBlur={(e) => { if (e.target.value !== n.clinicianName) saveN('clinicianName')(e.target.value) }} disabled={ro} /></label>
          <label className="field"><span>Title and qualifications</span><input defaultValue={n.clinicianTitle} onBlur={(e) => { if (e.target.value !== n.clinicianTitle) saveN('clinicianTitle')(e.target.value) }} disabled={ro} /></label>
          <label className="field"><span>GMC number</span><input defaultValue={n.clinicianGmc} onBlur={(e) => { if (e.target.value !== n.clinicianGmc) saveN('clinicianGmc')(e.target.value) }} disabled={ro} /></label>
        </div>
      </Card>

      <Card title="Lifestyle" subtitle="Your rating of each area from the questionnaire and the conversation; Good, Fair or Poor with one line of comment.">
        <table>
          <thead><tr><th>Area</th><th>Status</th><th>Comment</th></tr></thead>
          <tbody>
            {LIFESTYLE_AREAS.map(({ area, label }) => {
              const r = ratings.find((x) => x.area === area) ?? { area, status: null, comment: '' }
              const set = (patch: Partial<LifestyleRating>): void => setRatings((prev) => prev.map((x) => (x.area === area ? { ...x, ...patch } : x)))
              return (
                <tr key={area}>
                  <td>{label}</td>
                  <td>
                    <select value={r.status ?? ''} disabled={ro} onChange={(e) => set({ status: (e.target.value || null) as LifestyleStatus | null })}>
                      <option value="">—</option><option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
                    </select>
                  </td>
                  <td><input value={r.comment} disabled={ro} onChange={(e) => set({ comment: e.target.value })} style={{ width: '100%' }} /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {!ro && <button type="button" disabled={busy !== null} onClick={() => void run('lifestyle', () => api.setLifestyle(id, ratings), 'Lifestyle saved')}>Save lifestyle</button>}
      </Card>

      <Card title="Cancer risk and screening">
        <table>
          <thead><tr><th>Cancer</th><th>Increased risk?</th><th>Screen</th><th>Last screen</th><th>Result</th><th></th></tr></thead>
          <tbody>
            {screening.map((row, i) => {
              const set = (patch: Partial<ScreeningRow>): void => setScreening((prev) => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)))
              return (
                <tr key={row.id > 0 ? row.id : `new-${i}`}>
                  <td><input value={row.cancer} disabled={ro} onChange={(e) => set({ cancer: e.target.value })} /></td>
                  <td><select value={row.increasedRisk === null ? '' : row.increasedRisk ? 'yes' : 'no'} disabled={ro} onChange={(e) => set({ increasedRisk: e.target.value === '' ? null : e.target.value === 'yes' })}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select></td>
                  <td><input value={row.screenName} disabled={ro} onChange={(e) => set({ screenName: e.target.value })} /></td>
                  <td><input value={row.lastScreen} disabled={ro} onChange={(e) => set({ lastScreen: e.target.value })} /></td>
                  <td><input value={row.result} disabled={ro} onChange={(e) => set({ result: e.target.value })} /></td>
                  <td>{!ro && <button type="button" className="quiet" onClick={() => setScreening((prev) => prev.filter((_, j) => j !== i))}>remove</button>}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {!ro && (<p>
          <button type="button" className="quiet" onClick={() => setScreening((prev) => [...prev, { id: -Date.now(), cancer: '', increasedRisk: null, screenName: '', lastScreen: '', result: '' }])}>add row</button>{' '}
          <button type="button" disabled={busy !== null} onClick={() => void run('screening', () => api.setScreening(id, screening.map(({ cancer, increasedRisk, screenName, lastScreen, result }) => ({ cancer, increasedRisk, screenName, lastScreen, result }))), 'Screening saved')}>Save screening</button>
        </p>)}
        <TextField label="Doctor's comments on cancer risk" value={d.comments.find((c) => c.sectionCode === 'cancer')?.comment ?? ''} onSave={(v) => void run('c-cancer', () => api.setComment(id, 'cancer', v))} disabled={locked || !canComment} rows={3} />
      </Card>

      <Card title="Comments by section" subtitle="Printed under each section's results table. Saved when you leave the box.">
        {d.sections.map((s) => (
          <TextField key={s.code} label={`${s.commentHeading}: ${s.title}`} value={d.comments.find((c) => c.sectionCode === s.code)?.comment ?? ''} onSave={(v) => void run(`c-${s.code}`, () => api.setComment(id, s.code, v))} disabled={locked || !canComment} rows={3} />
        ))}
      </Card>
    </>
  )
}

// --- Report -----------------------------------------------------------------------------------

function ReportOptionsCard({ d, run, canEdit }: { d: AssessmentDetail; run: Run; canEdit: boolean }) {
  const o = d.reportOptions
  const withResults = d.sections.filter((s) => d.results.some((r) => r.sectionCode === s.code && (r.value !== null || r.valueText !== null)))
  const chosen = o.sections ?? withResults.map((s) => s.code)
  const save = (patch: Parameters<typeof api.setReportOptions>[1]): void => { void run('report-options', () => api.setReportOptions(d.assessment.id, patch)) }
  const toggle = (code: string): void => save({ sections: chosen.includes(code) ? chosen.filter((c) => c !== code) : [...chosen, code] })
  return (
    <Card title="Report type" subtitle="The full Prevent51 report, or a short letter with the sections you choose.">
      <div className="row">
        {(['full', 'summary'] as ReportType[]).map((t) => (
          <label key={t} className="check"><input type="radio" name="report-type" checked={o.type === t} disabled={!canEdit} onChange={() => save({ type: t })} /> {t === 'full' ? 'Full Prevent51 report' : 'Short results report'}</label>
        ))}
      </div>
      {o.type === 'summary' && (
        <>
          <p className="note">Sections to print. Only sections with at least one result are listed.</p>
          <div className="section-picks">
            {withResults.length === 0 && <span className="muted">No results entered yet.</span>}
            {withResults.map((s) => (
              <label key={s.code} className="check"><input type="checkbox" checked={chosen.includes(s.code)} disabled={!canEdit} onChange={() => toggle(s.code)} /> {s.title}</label>
            ))}
          </div>
          {canEdit && withResults.length > 0 && (
            <p><button type="button" className="quiet" onClick={() => save({ sections: null })}>all</button><button type="button" className="quiet" onClick={() => save({ sections: [] })}>none</button></p>
          )}
          <TextField label="Summary" hint="printed on page one, above the results; leave blank for none" value={o.overallComment} onSave={(v) => save({ overallComment: v })} disabled={!canEdit} rows={4} />
        </>
      )}
    </Card>
  )
}

function ReportTab({ d, busy, run, canSign, canEdit }: { d: AssessmentDetail; busy: string | null; run: Run; canSign: boolean; canEdit: boolean }) {
  const id = d.assessment.id
  const [nonce, setNonce] = useState(0)
  const locked = d.assessment.status === 'signed' || d.assessment.status === 'delivered'
  const open = d.exceptions.filter((e) => e.resolvedAt === null && e.severity === 'error').length
  return (
    <>
      <ReportOptionsCard d={d} run={run} canEdit={canEdit} />
      <Card title="Report" actions={<>
        <a className="button-link" href={api.draftPdfUrl(id)} target="_blank" rel="noreferrer">Open draft PDF</a>
        <button type="button" className="quiet" onClick={() => setNonce((n) => n + 1)}>Refresh preview</button>
        {canSign && !locked && <button type="button" disabled={busy !== null || open > 0} title={open > 0 ? 'Resolve the error-level exceptions first' : undefined} onClick={() => { if (window.confirm('Sign off this report? The values, ranges and comments are frozen into a numbered version.')) void run('sign', async () => (await api.generateReport(id)).detail, 'Signed. The PDF is stored as a version below.') }}>{busy === 'sign' ? 'Generating…' : 'Sign off and generate PDF'}</button>}
      </>}>
        {d.versions.length > 0 && (
          <table>
            <thead><tr><th>Version</th><th>Generated</th><th>By</th><th>Size</th><th>Semble</th><th></th></tr></thead>
            <tbody>
              {d.versions.map((v) => (
                <tr key={v.id}>
                  <td>v{v.version}</td><td>{dateTimeLabel(v.generatedAt)}</td><td>{v.generatedBy ?? ''}</td><td>{bytes(v.pdfBytes)}</td>
                  <td>{v.pushedAt !== null ? <Pill tone="ok">in Semble · {v.sembleDocumentTitle ?? v.sembleDocumentId}</Pill> : canSign && d.patient.sembleId !== null ? <button type="button" disabled={busy !== null} onClick={() => void run('push', async () => (await api.pushReport(id, v.version)).detail, 'Uploaded to the patient\'s Semble documents')}>{busy === 'push' ? 'Sending…' : 'Send to Semble'}</button> : <span className="muted">not linked to Semble</span>}</td>
                  <td><a className="button-link" href={api.versionPdfUrl(id, v.version)} target="_blank" rel="noreferrer">PDF</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="note">The preview below is the live draft, rendered exactly as the PDF will be. Signing freezes it.</p>
        <iframe key={nonce} className="preview" title="Report preview" src={api.previewUrl(id)} />
      </Card>
    </>
  )
}

// --- Sources -----------------------------------------------------------------------------------

function SourcesTab({ d, locked, busy, run, canEdit }: { d: AssessmentDetail; locked: boolean; busy: string | null; run: Run; canEdit: boolean }) {
  const id = d.assessment.id
  const [pullNote, setPullNote] = useState<string | null>(null)
  const [extracted, setExtracted] = useState<Awaited<ReturnType<typeof api.upload>>['extracted'] | null>(null)
  const [extractedDoc, setExtractedDoc] = useState<number | null>(null)

  async function upload(kind: 'glycanage_pdf' | 'dexa_pdf', file: File) {
    await run(`up-${kind}`, async () => {
      const r = await api.upload(id, kind, file.name, await file.arrayBuffer())
      if (r.extracted) { setExtracted(r.extracted); setExtractedDoc(r.documentId) }
    }, kind === 'dexa_pdf' ? 'DEXA stored. Enter the values on the Results tab; the file is listed below.' : undefined)
  }

  return (
    <>
      <Card title="Semble" actions={canEdit && !locked && d.patient.sembleId !== null && (
        <button type="button" disabled={busy !== null} onClick={() => void run('pull', async () => { const r = await api.pull(id); setPullNote(`${r.pull.labsMapped} lab values and ${r.pull.observationsMapped} observations mapped; ${r.pull.exceptionsRaised} exceptions raised.${r.pull.notes.length ? ' ' + r.pull.notes.join(' ') : ''}`); return r.detail })}>{busy === 'pull' ? 'Pulling…' : 'Pull lab results and observations'}</button>
      )}>
        {d.patient.sembleId === null ? <p className="note">This patient is not linked to Semble.</p> : <p className="note">Reads the patient's lab results and the nurse consultation from Semble and maps them to markers. Unmapped lines become exceptions.</p>}
        {pullNote !== null && <p className="flash">{pullNote}</p>}
      </Card>

      <Card title="Uploads" subtitle="GlycanAge reports are read automatically; DEXA printouts are photographs, so their values are typed in on the Results tab.">
        {canEdit && !locked && (
          <div className="row">
            <label className="field"><span>GlycanAge PDF</span><input type="file" accept="application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload('glycanage_pdf', f) }} /></label>
            <label className="field"><span>DEXA PDF</span><input type="file" accept="application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload('dexa_pdf', f) }} /></label>
          </div>
        )}
        {extracted !== null && (
          <div className="extract">
            <p><b>Read from the GlycanAge report</b>{extracted.kitId && ` · kit ${extracted.kitId}`}{extracted.sampledOn && ` · sampled ${dateLabel(extracted.sampledOn)}`}. Check each value, then confirm.</p>
            <table><thead><tr><th>Value</th><th>Found</th><th>Evidence</th></tr></thead><tbody>
              {extracted.values.map((v) => <tr key={v.markerCode}><td>{v.label}</td><td><b>{v.value}</b></td><td className="muted small">{v.evidence}</td></tr>)}
            </tbody></table>
            {extracted.notes.length > 0 && <p className="warn-note">{extracted.notes.join('; ')}</p>}
            <button type="button" disabled={busy !== null || extracted.values.length === 0} onClick={() => void run('confirm-gly', () => api.setResults(id, extracted.values.map((v) => ({ markerCode: v.markerCode, value: v.value, measuredOn: extracted.sampledOn, sourceDocumentId: extractedDoc, sourceLocator: v.evidence.slice(0, 80) }))), 'GlycanAge values saved').then(() => setExtracted(null))}>Confirm and save</button>{' '}
            <button type="button" className="quiet" onClick={() => setExtracted(null)}>Discard</button>
          </div>
        )}
        <table>
          <thead><tr><th>Source</th><th>Kind</th><th>Status</th><th>Added</th><th></th></tr></thead>
          <tbody>
            {d.sources.map((s) => (
              <tr key={s.id}>
                <td>{s.label}{s.notes && <div className="muted small">{s.notes}</div>}</td>
                <td className="muted">{s.kind.replace('_', ' ')}</td>
                <td><Pill tone={s.extractionStatus === 'extracted' ? 'ok' : s.extractionStatus === 'failed' ? 'bad' : 'neutral'}>{s.extractionStatus}</Pill></td>
                <td className="muted small">{dateTimeLabel(s.uploadedAt)}{s.uploadedBy && ` · ${s.uploadedBy}`}</td>
                <td>{s.fileName !== null && <a className="button-link" href={api.documentUrl(id, s.id)} target="_blank" rel="noreferrer">open</a>}</td>
              </tr>
            ))}
            {d.sources.length === 0 && <tr><td colSpan={5} className="muted">Nothing yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="Exceptions" subtitle="Anything that could not be mapped, converted or reconciled. Nothing is dropped silently.">
        {d.exceptions.length === 0 ? <p className="note">None.</p> : (
          <table>
            <thead><tr><th></th><th>Code</th><th>Message</th><th>Raised</th><th></th></tr></thead>
            <tbody>
              {d.exceptions.map((e) => (
                <tr key={e.id} className={e.resolvedAt !== null ? 'muted' : undefined}>
                  <td><Pill tone={e.resolvedAt !== null ? 'neutral' : e.severity === 'error' ? 'bad' : e.severity === 'warning' ? 'warn' : 'neutral'}>{e.resolvedAt !== null ? 'resolved' : e.severity}</Pill></td>
                  <td className="mono">{e.code}</td><td>{e.message}</td><td className="muted small">{dateTimeLabel(e.raisedAt)}</td>
                  <td>{e.resolvedAt === null && canEdit && <button type="button" className="quiet" onClick={() => void run(`ex-${e.id}`, () => api.resolveException(id, e.id))}>resolve</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}

export { ApiRequestError }
